/** The resident chart's tabs, in order. The keys (`family`, `auth`, `hnp`, `careplans`…) are not
 * this app's route names, because they are what a reader's pinned tabs and custom order are saved
 * under in the browser, and what `?tab=` links carry. Renaming them would reset somebody's pins. */
export const RESIDENT_TABS = [
  { value: "profile", label: "Profile", segment: "", to: "/residents/$residentId" },
  { value: "move-in", label: "Move-in", segment: "move-in", to: "/residents/$residentId/move-in" },
  {
    value: "timeline",
    label: "Timeline",
    segment: "timeline",
    to: "/residents/$residentId/timeline",
  },
  { value: "family", label: "Family", segment: "family", to: "/residents/$residentId/family" },
  {
    value: "engagement",
    label: "Engagement",
    segment: "engagement",
    to: "/residents/$residentId/engagement",
  },
  {
    value: "primary-care",
    label: "Primary care",
    segment: "primary-care",
    to: "/residents/$residentId/primary-care",
  },
  {
    value: "institutions",
    label: "Institutions",
    segment: "institutions",
    to: "/residents/$residentId/institutions",
  },
  { value: "notes", label: "Notes", segment: "notes", to: "/residents/$residentId/notes" },
  {
    value: "therapies",
    label: "Therapies",
    segment: "therapies",
    to: "/residents/$residentId/therapies",
  },
  {
    value: "facesheet",
    label: "Face sheet",
    segment: "facesheet",
    to: "/residents/$residentId/facesheet",
  },
  {
    value: "auth",
    label: "Auth",
    segment: "authorizations",
    to: "/residents/$residentId/authorizations",
  },
  {
    value: "financials",
    label: "Financials",
    segment: "statement",
    to: "/residents/$residentId/statement",
  },
  {
    value: "financial-details",
    label: "Income & assets",
    segment: "financials",
    to: "/residents/$residentId/financials",
  },
  { value: "vitals", label: "Vitals", segment: "vitals", to: "/residents/$residentId/vitals" },
  {
    value: "hnp",
    label: "H&P",
    segment: "medical-records",
    to: "/residents/$residentId/medical-records",
  },
  { value: "careplans", label: "Care plans", segment: "plans", to: "/residents/$residentId/plans" },
  {
    value: "physician-poc",
    label: "Physician POC",
    segment: "physician-poc",
    to: "/residents/$residentId/physician-poc",
  },
  {
    value: "orders",
    label: "Orders",
    segment: "medications",
    to: "/residents/$residentId/medications",
  },
  {
    value: "appointments",
    label: "Appointments",
    segment: "appointments",
    to: "/residents/$residentId/appointments",
  },
  { value: "medications", label: "Medications", segment: "mar", to: "/residents/$residentId/mar" },
  {
    value: "care-pass",
    label: "Care pass",
    segment: "care-pass",
    to: "/residents/$residentId/care-pass",
  },
  {
    value: "documents",
    label: "Documents",
    segment: "documents",
    to: "/residents/$residentId/documents",
  },
  {
    value: "admission-packet",
    label: "Admission packet",
    segment: "admission-packet",
    to: "/residents/$residentId/admission-packet",
  },
  {
    value: "activities",
    label: "Activities",
    segment: "activities",
    to: "/residents/$residentId/activities",
  },
  {
    value: "incidents",
    label: "Incidents",
    segment: "incidents",
    to: "/residents/$residentId/incidents",
  },
  {
    value: "visitors",
    label: "Visitors",
    segment: "visitors",
    to: "/residents/$residentId/visitors",
  },
  {
    value: "outings",
    label: "Outings",
    segment: "outings",
    to: "/residents/$residentId/outings",
  },
  {
    value: "attendance",
    label: "Attendance",
    segment: "attendance",
    to: "/residents/$residentId/attendance",
  },
  {
    value: "checklists",
    label: "Checklists",
    segment: "checklists",
    to: "/residents/$residentId/checklists",
  },
  {
    value: "trust-fund",
    label: "Trust fund",
    segment: "trust-fund",
    to: "/residents/$residentId/trust-fund",
  },
  /* Not a tab on the live chart: the problem list and diagnoses it keeps are
     this app's, so it sits last, where nobody's habits are in its way. */
  {
    value: "clinical",
    label: "Clinical",
    segment: "clinical",
    to: "/residents/$residentId/clinical",
  },
] as const;

export type ResidentTab = (typeof RESIDENT_TABS)[number];
export type ResidentTabValue = ResidentTab["value"];

export const DEFAULT_VISIBLE_TABS: readonly string[] = [
  "profile",
  "timeline",
  "family",
  "primary-care",
  "auth",
  "financials",
  "notes",
];

/** Pages under a tab that are not the tab itself, by their first path segment. */
const NESTED: Record<string, ResidentTabValue> = {
  "plans-of-care": "careplans",
  "al-orders": "physician-poc",
  "therapy-notes": "therapies",
  "therapy-referrals": "therapies",
  "admission-packets": "admission-packet",
};

const FAVORITES_KEY = "resident-detail-favorite-tabs";
const ORDER_KEY = "resident-detail-tab-order";
const VALUES: readonly string[] = RESIDENT_TABS.map((tab) => tab.value);

export function tabByValue(value: string): ResidentTab | undefined {
  return RESIDENT_TABS.find((tab) => tab.value === value);
}

/** Which tab a chart path is on: `/residents/<id>/<segment>/...`. */
export function tabForPath(pathname: string): ResidentTabValue {
  const segment = pathname.split("/").filter(Boolean)[2] ?? "";
  return (
    RESIDENT_TABS.find((tab) => tab.segment === segment)?.value ?? NESTED[segment] ?? "profile"
  );
}

function readList(key: string): string[] | null {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(key) ?? "null");
    return Array.isArray(parsed) ? parsed.filter((value) => VALUES.includes(value)) : null;
  } catch {
    return null;
  }
}

function writeList(key: string, values: string[]) {
  try {
    localStorage.setItem(key, JSON.stringify(values));
  } catch {
    /* A browser that refuses storage still gets a working strip, just unsaved. */
  }
}

export const loadFavoriteTabs = (): string[] => readList(FAVORITES_KEY) ?? [];
export const saveFavoriteTabs = (values: string[]) => writeList(FAVORITES_KEY, values);

/** The saved order, with any tab added since appended where it naturally falls. */
export function loadTabOrder(): string[] {
  const stored = readList(ORDER_KEY) ?? [];
  return [...stored, ...VALUES.filter((value) => !stored.includes(value))];
}
export const saveTabOrder = (values: string[]) => writeList(ORDER_KEY, values);

/** Pinned first in saved order, then the rest in saved order. */
export function displayOrder(allowed: readonly string[], favorites: string[], order: string[]) {
  const rank = new Map(order.map((value, index) => [value, index]));
  const byRank = (a: string, b: string) => (rank.get(a) ?? 999) - (rank.get(b) ?? 999);
  const pinned = favorites.filter((value) => allowed.includes(value)).toSorted(byRank);
  return [...pinned, ...allowed.filter((value) => !pinned.includes(value)).toSorted(byRank)];
}

/** Moves `dragged` into `target`'s place. */
export function reorder(order: string[], dragged: string, target: string): string[] {
  if (dragged === target) return order;
  const without = order.filter((value) => value !== dragged);
  const at = without.indexOf(target);
  if (at === -1) return order;
  return [...without.slice(0, at), dragged, ...without.slice(at)];
}

export { fitTabs } from "@/components/app/patterns/fit-tabs";
